import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './Products.css';
import './AllSellStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import apiService from '../../services/api';

const CustomerSupplierReport = () => {
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Applied Filters
  const [contactType, setContactType] = useState('all');
  const [locationId, setLocationId] = useState('all');
  const [customerGroupId, setCustomerGroupId] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pending Filters inside modal
  const [pendingContactType, setPendingContactType] = useState('all');
  const [pendingLocationId, setPendingLocationId] = useState('all');
  const [pendingCustomerGroupId, setPendingCustomerGroupId] = useState('all');
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');

  // Summary Metrics
  const [summary, setSummary] = useState({
    totalPurchase: 0,
    totalPurchaseReturn: 0,
    totalSale: 0,
    totalSellReturn: 0,
    totalOpeningBalanceDue: 0,
    totalDue: 0,
  });

  const fetchFilterOptions = async () => {
    try {
      const res = await apiService.getCustomerSupplierReportFilterOptions();
      const options = res.data || res;
      setLocations(options.locations || []);
      setCustomerGroups(options.customer_groups || []);
    } catch (err) {
      /* console.error('Error fetching filter options:', err); */
    }
  };

  const fetchReportData = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: entriesPerPage,
      };

      if (contactType !== 'all') params.contact_type = contactType;
      if (locationId !== 'all') params.location_id = locationId;
      if (customerGroupId !== 'all') params.customer_group_id = customerGroupId;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await apiService.getCustomerSupplierReport(params);
      const dataObj = response.data || response;
      const contactList = dataObj.contacts || (Array.isArray(dataObj) ? dataObj : []);
      setContacts(contactList);

      const pagination = dataObj.pagination || {};
      setTotalRecords(pagination.total || contactList.length);

      if (dataObj.summary) {
        setSummary({
          totalPurchase: dataObj.summary.total_purchase || 0,
          totalPurchaseReturn: dataObj.summary.total_purchase_return || 0,
          totalSale: dataObj.summary.total_sale || 0,
          totalSellReturn: dataObj.summary.total_sell_return || 0,
          totalOpeningBalanceDue: dataObj.summary.opening_balance_due || 0,
          totalDue: dataObj.summary.due || 0,
        });
      } else {
        let purchase = 0;
        let purchaseReturn = 0;
        let sale = 0;
        let sellReturn = 0;
        let obDue = 0;
        let due = 0;

        contactList.forEach((c) => {
          purchase += parseFloat(c.total_purchase || 0);
          purchaseReturn += parseFloat(c.total_purchase_return || 0);
          sale += parseFloat(c.total_sale || 0);
          sellReturn += parseFloat(c.total_sell_return || 0);
          obDue += parseFloat(c.opening_balance_due || 0);
          due += parseFloat(c.due || 0);
        });

        setSummary({
          totalPurchase: purchase,
          totalPurchaseReturn: purchaseReturn,
          totalSale: sale,
          totalSellReturn: sellReturn,
          totalOpeningBalanceDue: obDue,
          totalDue: due,
        });
      }
    } catch (err) {
      /* console.error('Failed to fetch customer supplier report data', err); */
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchReportData();
  }, [currentPage, entriesPerPage, contactType, locationId, customerGroupId, startDate, endDate]);

  const handleOpenFilters = () => {
    setPendingContactType(contactType);
    setPendingLocationId(locationId);
    setPendingCustomerGroupId(customerGroupId);
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    setContactType(pendingContactType);
    setLocationId(pendingLocationId);
    setCustomerGroupId(pendingCustomerGroupId);
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    setPendingContactType('all');
    setPendingLocationId('all');
    setPendingCustomerGroupId('all');
    setPendingStartDate('');
    setPendingEndDate('');
  };

  const filteredContacts = contacts.filter((c) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = (c.name || '').toLowerCase();
    const businessName = (c.supplier_business_name || '').toLowerCase();
    const mobile = (c.mobile || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    return name.includes(term) || businessName.includes(term) || mobile.includes(term) || email.includes(term);
  });

  const tableHeaders = [
    'CONTACT / BUSINESS NAME',
    'TYPE',
    'TOTAL PURCHASE',
    'TOTAL PURCHASE RETURN',
    'TOTAL SALE',
    'TOTAL SELL RETURN',
    'OPENING BALANCE DUE',
    'TOTAL DUE',
  ];

  const handleExport = (format) => {
    const headers = tableHeaders;
    const rows = filteredContacts.map((c) => [
      c.supplier_business_name ? c.supplier_business_name : (c.name || '-'),
      (c.contact_type || '-').toUpperCase(),
      `$${parseFloat(c.total_purchase || 0).toFixed(2)}`,
      `$${parseFloat(c.total_purchase_return || 0).toFixed(2)}`,
      `$${parseFloat(c.total_sale || 0).toFixed(2)}`,
      `$${parseFloat(c.total_sell_return || 0).toFixed(2)}`,
      `$${parseFloat(c.opening_balance_due || 0).toFixed(2)}`,
      `$${parseFloat(c.due || 0).toFixed(2)}`,
    ]);

    const title = 'Supplier & Customer Report';
    const filename = 'supplier_customer_report';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

  return (
    <div style={{ paddingTop: '0.5rem' }}>
      {/* ── Summary Metric Cards ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.25rem',
        }}
      >
        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Purchase</span>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#2563eb', margin: '0.2rem 0 0' }}>{formatCurrency(summary.totalPurchase)}</h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Purchase Return</span>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#6366f1', margin: '0.2rem 0 0' }}>{formatCurrency(summary.totalPurchaseReturn)}</h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Sale</span>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981', margin: '0.2rem 0 0' }}>{formatCurrency(summary.totalSale)}</h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Sell Return</span>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#ec4899', margin: '0.2rem 0 0' }}>{formatCurrency(summary.totalSellReturn)}</h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Opening Balance Due</span>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#f59e0b', margin: '0.2rem 0 0' }}>{formatCurrency(summary.totalOpeningBalanceDue)}</h3>
        </div>

        <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Due</span>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '700', color: '#ef4444', margin: '0.2rem 0 0' }}>{formatCurrency(summary.totalDue)}</h3>
        </div>
      </div>

      {/* ── Table Container ── */}
      <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {/* Table controls */}
        <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1rem 1.25rem', position: 'relative', zIndex: 20 }}>
          <div className="table-controls-left">
            <div className="entries-select">
              <span>Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Filters Button placed right before Export Button */}
            <button
              onClick={handleOpenFilters}
              style={{
                padding: '0.4rem 0.8rem',
                border: '1px solid var(--etpos-border, #e2e8f0)',
                borderRadius: '6px',
                background: 'var(--etpos-card, #fff)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: 'var(--etpos-text, #334155)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontWeight: '500'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
              </svg>
              Filters
            </button>

            <div className="export-dropdown-container" style={{ position: 'relative', zIndex: 100 }}>
              <button
                className="export-btn"
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#334155' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div
                  className="export-dropdown-menu"
                  style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', zIndex: 9999, minWidth: '160px' }}
                >
                  {[
                    { label: 'Export CSV', action: () => handleExport('CSV'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
                    { label: 'Export Excel', action: () => handleExport('Excel'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M8 13h8"/><path d="M8 17h8"/></svg> },
                    { label: 'Print', action: () => handleExport('Print'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg> },
                    { label: 'Export PDF', action: () => handleExport('PDF'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15v-6h2a2 2 0 0 1 0 4H9"/></svg> }
                  ].map((item, i) => (
                    <button
                      key={i}
                      onClick={() => { item.action(); setShowExportDropdown(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.55rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: '#475569' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="text"
              placeholder="Search ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '0.4rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px' }}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {tableHeaders.map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={tableHeaders.length} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                    Loading...
                  </td>
                </tr>
              ) : filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan={tableHeaders.length} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                    No contact records found
                  </td>
                </tr>
              ) : (
                filteredContacts.map((contact) => {
                  const type = (contact.contact_type || 'customer').toLowerCase();
                  const typeBadgeColor =
                    type === 'customer' ? '#2563eb' : type === 'supplier' ? '#d97706' : '#059669';
                  const typeBgColor =
                    type === 'customer' ? '#eff6ff' : type === 'supplier' ? '#fffbe6' : '#ecfdf5';

                  const displayName = contact.supplier_business_name ? contact.supplier_business_name : (contact.name || '-');

                  return (
                    <tr key={contact.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: '#1e293b' }}>{displayName}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', color: typeBadgeColor, background: typeBgColor }}>
                          {type.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{formatCurrency(contact.total_purchase)}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{formatCurrency(contact.total_purchase_return)}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569', fontWeight: '600' }}>{formatCurrency(contact.total_sale)}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{formatCurrency(contact.total_sell_return)}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{formatCurrency(contact.opening_balance_due)}</td>
                      <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '700', color: contact.due > 0 ? '#ef4444' : '#10b981' }}>
                        {formatCurrency(contact.due)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: '700' }}>
                <td colSpan="2" style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', textAlign: 'right', zIndex: 5 }}>
                  Total:
                </td>
                <td style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap', zIndex: 5 }}>
                  {formatCurrency(summary.totalPurchase)}
                </td>
                <td style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap', zIndex: 5 }}>
                  {formatCurrency(summary.totalPurchaseReturn)}
                </td>
                <td style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap', zIndex: 5 }}>
                  {formatCurrency(summary.totalSale)}
                </td>
                <td style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap', zIndex: 5 }}>
                  {formatCurrency(summary.totalSellReturn)}
                </td>
                <td style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap', zIndex: 5 }}>
                  {formatCurrency(summary.totalOpeningBalanceDue)}
                </td>
                <td style={{ position: 'sticky', bottom: 0, background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', padding: '0.75rem 1rem', fontSize: '0.875rem', color: summary.totalDue > 0 ? '#ef4444' : '#10b981', fontWeight: '700', whiteSpace: 'nowrap', zIndex: 5 }}>
                  {formatCurrency(summary.totalDue)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #e2e8f0' }}>
          <div className="pagination-info" style={{ fontSize: '0.8125rem', color: '#475569' }}>
            Showing {filteredContacts.length > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0} to{' '}
            {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords} entries
          </div>
          <div className="pagination-controls" style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="pagination-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', color: '#475569' }}>
              Page {currentPage}
            </span>
            <button
              className="pagination-btn"
              disabled={currentPage * entriesPerPage >= totalRecords}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ── Filters Popup Modal ── */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%', background: 'var(--etpos-card, #fff)' }}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', color: 'var(--etpos-text, #111827)' }}>
              <h2 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filter Supplier & Customer Report
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', maxWidth: 'none' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Contact Type:</label>
                  <select value={pendingContactType} onChange={e => setPendingContactType(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }}>
                    <option value="all">All Contact Types</option>
                    <option value="customer">Customer</option>
                    <option value="supplier">Supplier</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Location:</label>
                  <select value={pendingLocationId} onChange={e => setPendingLocationId(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }}>
                    <option value="all">All Locations</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Start Date:</label>
                  <input type="date" value={pendingStartDate} onChange={e => setPendingStartDate(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>End Date:</label>
                  <input type="date" value={pendingEndDate} onChange={e => setPendingEndDate(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #475569)', marginBottom: '0.35rem' }}>Customer Group:</label>
                <select value={pendingCustomerGroupId} onChange={e => setPendingCustomerGroupId(e.target.value)} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', outline: 'none', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #1e293b)' }}>
                  <option value="all">All Customer Groups</option>
                  {customerGroups.map(cg => <option key={cg.id} value={cg.id}>{cg.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', background: 'var(--etpos-card, #fff)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={handleResetFilters}
                style={{
                  height: '38px',
                  padding: '0 1.25rem',
                  border: '1px solid var(--etpos-border, #cbd5e1)',
                  borderRadius: '6px',
                  background: 'var(--etpos-card, #f8fafc)',
                  color: 'var(--etpos-text, #475569)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease'
                }}
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                style={{
                  height: '38px',
                  padding: '0 1.25rem',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#3b82f6',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(59,130,246,0.2)',
                  transition: 'all 0.15s ease'
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSupplierReport;
